import { EngineInstance } from '../../../engine_wrapper'
import createDefaultStateConfig from '../state/create_default_state_config'
import createDefaultStateMap from '../state/create_default_state_map'
import type { State } from '../state/State.type'
import { StateMapDisk } from '../state/state_map_disk.enum'
import { StateType } from '../state/state_type.enum'
import { createNewRNGConfig } from '../rng'
import type { Engine } from './Engine.type'
import { decodeStateSnapshotUnchecked, encodeStateMessage } from './engineWasmCodec'
import { getEngineWasmSnapshotErrorMessage } from './engineWasmErrors'

export type EngineWasmSession = {
  handle: number
  instance: EngineInstance
}

export type EngineWasmManager = {
  lastResetError: Error | null
  resetCounter: number
  sessionPromise: Promise<EngineWasmSession> | null
}

export function createEngineWasmManager(): EngineWasmManager {
  return {
    lastResetError: null,
    resetCounter: 0,
    sessionPromise: null,
  }
}

export function resetEngineWasmSession(engine: Engine, state: State = engine.state): Promise<EngineWasmSession> {
  return resetEngineWasmSessionFromStateMessage(engine, encodeStateMessage(state))
}

export function resetEngineWasmSessionFromStateMessage(
  engine: Engine,
  stateBytes: Uint8Array
): Promise<EngineWasmSession> {
  const resetCounter = ++engine.wasm.resetCounter
  const sessionPromise = EngineInstance.create()
    .then((instance) => {
      const handle = instance.createGameFromState(stateBytes)
      return {
        handle,
        instance,
      }
    })
    .then((session) => {
      if (engine.wasm.resetCounter !== resetCounter) {
        return engine.wasm.sessionPromise as Promise<EngineWasmSession>
      }
      engine.wasm.lastResetError = null
      return session
    })
    .catch((error: unknown) => {
      const err = error instanceof Error ? error : new Error(String(error))
      engine.wasm.lastResetError = err
      throw err
    })

  engine.wasm.sessionPromise = sessionPromise
  return sessionPromise
}

export function ensureEngineWasmSession(engine: Engine): Promise<EngineWasmSession> {
  return engine.wasm.sessionPromise ?? resetEngineWasmSession(engine)
}

function getPreferredLocalDisk(state: State): StateMapDisk.IndexedDB | StateMapDisk.Memory | undefined {
  if (state.map.disk === StateMapDisk.IndexedDB || state.map.disk === StateMapDisk.Memory) {
    return state.map.disk
  }
}

/**
 * Constructor/bootstrap helper for code that still needs a single extra
 * map-editor player appended through the WASM path.
 */
export async function appendMapEditorPlayerToState(state: State): Promise<State> {
  const instance = await EngineInstance.create()
  const snapshot = instance.appendMapEditorPlayer(encodeStateMessage(state))
  return decodeStateSnapshotUnchecked(snapshot, getPreferredLocalDisk(state))
}

/**
 * WASM-backed constructor seed used by default-new-game and map-builder
 * flows. Callers may mutate the scaffold before the map-editor players are
 * appended.
 */
export async function createStateFromWasmBootstrap(
  width: number,
  height: number,
  disk: StateMapDisk,
  configureState?: (state: State) => void | Promise<void>
): Promise<State> {
  const instance = await EngineInstance.create()
  const snapshot = instance.getBlankStateForEngineInit()
  let state = decodeStateSnapshotUnchecked(snapshot)

  state.round = 1
  state.online = null
  state.type = StateType.Map
  state.config = createDefaultStateConfig()
  state.map = createDefaultStateMap(disk)
  state.width = width
  state.height = height
  state.ents = []
  state.players = []
  state.turnPlayerId = null
  state.rng = createNewRNGConfig()

  if (configureState) {
    await configureState(state)
  }

  state = await appendMapEditorPlayerToState(state)
  state = await appendMapEditorPlayerToState(state)

  return state
}

/**
 * WASM-backed default new-game constructor.
 *
 * This intentionally preserves the current default-new-game scaffold while
 * moving the root seed and player creation through the engine bridge.
 */
export async function createDefaultNewGameStateFromWasm(
  width: number,
  height: number,
  disk: StateMapDisk
): Promise<State> {
  const state = await createStateFromWasmBootstrap(width, height, disk)

  state.round = 1
  state.online = null
  state.type = StateType.Map
  state.config = createDefaultStateConfig()
  state.map = createDefaultStateMap(disk)
  state.width = width
  state.height = height
  state.ents = []
  state.turnPlayerId = null
  state.rng = createNewRNGConfig()

  return state
}

export function getEngineWasmErrorMessage(session: EngineWasmSession): string | null {
  try {
    return getEngineWasmSnapshotErrorMessage(session.instance.getFullState(session.handle))
  } catch {
    // keep original error path
  }
  return null
}

// Transitional re-exports while callers are migrated off engineWasm.ts.
export {
  decodeStateMessageBytes,
  decodeStateSnapshot,
  encodeActionMessage,
  encodeStateMessage,
} from './engineWasmCodec'
export {
  getEngineWasmCallErrorMessage,
  getEngineWasmSnapshotErrorMessage,
} from './engineWasmErrors'
